/**
 * Calculation utilities for the Analytics page
 */

import type { WorkerAssignmentLog, TaskType } from 'src/store/todo/types'

/**
 * Better color palette for task types - distinct and visible
 */
export const TASK_COLOR_PALETTE = ['#1B6EFF', '#39A64D', '#8667FF', '#00A5A7', '#FF9F1C']

/**
 * Get a consistent color for a task type based on its index
 *
 * @param taskTypeId - ID of the task type
 * @param tasks - Array of all task types
 * @returns Hex color code
 */
export const getTaskColor = (taskTypeId: string, tasks: TaskType[]): string => {
  const taskIndex = tasks.findIndex((t) => t.id === taskTypeId)
  if (taskIndex === -1) return '#A3A3A8' // default gray
  return TASK_COLOR_PALETTE[taskIndex % TASK_COLOR_PALETTE.length]
}

/**
 * Check if a task is a value-type task (e.g., HVST - harvest)
 * Value-type tasks track quantities (boxes, units) rather than just completion
 *
 * @param taskTypeId - ID of the task type
 * @param tasksMap - Map of task IDs to TaskType objects
 * @returns true if this is a value-type task
 */
export const isValueTypeTask = (
  taskTypeId: string,
  tasksMap: Record<string, TaskType>
): boolean => {
  const task = tasksMap[taskTypeId]
  return task?.taskTypeMoniker === 'HVST'
}

/**
 * Get all logs for a specific assignment
 *
 * @param logs - Array of all worker assignment logs
 * @param workerId - ID of the worker
 * @param taskTypeId - ID of the task type
 * @param zoneId - ID of the zone
 * @param locationId - Optional location ID
 * @returns Filtered array of logs
 */
export const getAssignmentLogs = (
  logs: WorkerAssignmentLog[],
  workerId: string,
  taskTypeId: string,
  zoneId: string,
  locationId?: string
): WorkerAssignmentLog[] => {
  return logs.filter(
    (log) =>
      log.workerId === workerId &&
      log.taskTypeId === taskTypeId &&
      log.zoneId === zoneId &&
      log.locationId === locationId
  )
}

/**
 * Get total value for an assignment across all logs (active ones only)
 *
 * @param logs - Array of all worker assignment logs
 * @param workerId - ID of the worker
 * @param taskTypeId - ID of the task type
 * @param zoneId - ID of the zone
 * @param locationId - Optional location ID
 * @returns Sum of all active log values
 */
export const getAssignmentTotalValue = (
  logs: WorkerAssignmentLog[],
  workerId: string,
  taskTypeId: string,
  zoneId: string,
  locationId?: string
): number => {
  return getAssignmentLogs(logs, workerId, taskTypeId, zoneId, locationId)
    .filter((log) => !log.softDeleted)
    .reduce((sum, log) => sum + log.value, 0)
}

/**
 * Get total value for a zone-level assignment (sum of all location-level logs)
 * Used for HVST tasks to aggregate boxes across all locations in a zone
 *
 * @param logs - Array of all worker assignment logs
 * @param workerId - ID of the worker
 * @param taskTypeId - ID of the task type
 * @param zoneId - ID of the zone
 * @returns Sum of all location-level log values in this zone
 */
export const getAssignmentZoneTotalValue = (
  logs: WorkerAssignmentLog[],
  workerId: string,
  taskTypeId: string,
  zoneId: string
): number => {
  return logs
    .filter(
      (log) =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        log.zoneId === zoneId &&
        log.locationId && // Only include location-level logs
        !log.softDeleted
    )
    .reduce((sum, log) => sum + log.value, 0)
}

/**
 * Get zone-level total (logs without locationId for this worker+task+zone)
 * These are logs that apply to the entire zone, not specific locations
 *
 * @param logs - Array of all worker assignment logs
 * @param workerId - ID of the worker
 * @param taskTypeId - ID of the task type
 * @param zoneId - ID of the zone
 * @returns Sum of zone-level log values
 */
export const getZoneLevelTotal = (
  logs: WorkerAssignmentLog[],
  workerId: string,
  taskTypeId: string,
  zoneId: string
): number => {
  return logs
    .filter(
      (log) =>
        log.workerId === workerId &&
        log.taskTypeId === taskTypeId &&
        log.zoneId === zoneId &&
        !log.locationId && // Only zone-level logs (no location)
        !log.softDeleted
    )
    .reduce((sum, log) => sum + log.value, 0)
}

/**
 * Calculate percentage with safe division
 *
 * @param value - Numerator value
 * @param total - Denominator value
 * @returns Percentage rounded to nearest integer, or 0 if total is 0
 */
export const calculatePercentage = (value: number, total: number): number => {
  return total > 0 ? Math.round((value / total) * 100) : 0
}
